/*
 * Copyright (C) 2020 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface PageImage {
  data: string;
  width: number;
  height: number;
}

export interface ImageUrlMapping {
  [url: string]: string;
}

export class PrintPageHelper {
  static splitHtmlInToFragments(
    html: string | HTMLElement,
    maxSize: number,
    computedData = { result: [''] as string[], addAncestorTags: false },
  ): string[] {
    if (!(html instanceof HTMLElement)) {
      const element = document.createElement('div');
      element.id = 'temporary_root';
      element.innerHTML = html;
      html = element;
    }

    for (let i = 0; i < html.childNodes.length; ++i) {
      if (html.childNodes[i] instanceof HTMLOListElement) {
        const list = html.childNodes[i] as HTMLOListElement;
        list.style.listStyle = 'none';
        list.style.counterReset = 'ol-counter';
      } else if (html.childNodes[i].parentElement instanceof HTMLOListElement) {
        const list = html.childNodes[i].parentElement as HTMLOListElement;
        const item = html.childNodes[i] as HTMLLIElement;
        list.style.counterReset = 'ol-counter ' + i;
        item.classList.add('uses-counter');
      }

      if (computedData.addAncestorTags) {
        computedData.result.push(this.extractElementOpeningTags(html.childNodes[i], true));
        computedData.addAncestorTags = false;
        maxSize = 100;
      } else {
        computedData.result[computedData.result.length - 1] += this.extractElementOpeningTags(html.childNodes[i]);
      }

      if (html.childNodes[i] instanceof HTMLElement && !(html.childNodes[i] instanceof HTMLBRElement)) {
        this.splitHtmlInToFragments(html.childNodes[i] as HTMLElement, maxSize, computedData);
      }

      if (this.getHtmlContentSize(computedData.result[computedData.result.length - 1]) >= maxSize) {
        if (!computedData.addAncestorTags) {
          maxSize = 100;
          computedData.addAncestorTags = true;
          computedData.result[computedData.result.length - 1] += this.extractElementClosingTags(html.childNodes[i], true);
        }
      } else {
        computedData.result[computedData.result.length - 1] += this.extractElementClosingTags(html.childNodes[i]);
      }
    }

    return computedData.result;
  }

  static getHtmlContentSize(html: string, width = 950): number {
    let size = 5;

    if (html) {
      const element = document.createElement('div');
      element.className = 'preview-element';
      element.style.position = 'fixed';
      element.style.width = `${width}px`;
      element.style.top = '0';
      element.style.left = '0';
      element.innerHTML = html;
      document.body.appendChild(element);
      size += element.clientHeight / 13;
      document.body.removeChild(element);
    }

    return size;
  }

  static fetchImageDataUrls(fetchPromises: Promise<Response>[]): Promise<ImageUrlMapping> {
    return new Promise<ImageUrlMapping>((mappingResolve) => {
      /* eslint-disable */
      Promise.all(fetchPromises.map((p) => p.catch(() => null))).then((responses) => {
        const blobPromises = responses
          .filter((response) => response?.status === 200)
          .map((response) => {
            return {
              blob: response.blob(),
              url: response.url,
            };
          });

        Promise.all(
          blobPromises.map((blob) =>
            blob.blob.then((res) => {
              return {
                blob: res,
                url: blob.url,
              };
            }),
          ),
        ).then((blobs) => {
          const dataUrlPromises: Promise<{ result: any; url: string }>[] = blobs.map((blob) => {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.readAsDataURL(blob.blob);
              reader.onload = () => resolve({ result: reader.result, url: blob.url });
            });
          });

          Promise.all(dataUrlPromises).then((dataUrls) => {
            const imageUrlMapping: ImageUrlMapping = {};

            for (const dataUrl of dataUrls) {
              imageUrlMapping[dataUrl.url] = dataUrl.result;
            }

            mappingResolve(imageUrlMapping);
          });
        });
      });
      /* eslint-enable */
    });
  }

  private static readonly MAX_CANVAS_PIXELS = 24_000_000;

  static async addPages(pages: NodeListOf<Element>): Promise<jsPDF> {
    const elements = Array.from(pages) as HTMLElement[];

    if (!elements.length) {
      return new jsPDF();
    }

    const images = await this.renderPages(elements);
    const doc = new jsPDF();

    for (let i = 0; i < images.length; ++i) {
      const image = images[i];
      const page = elements[i];
      const links = page.getElementsByTagName('a');

      doc.addImage({
        imageData: image.data,
        x: 10,
        y: 10,
        width: image.width / 6,
        height: image.height / 6,
      });

      for (let index = 0; index < links.length; ++index) {
        const pageRect = page.getBoundingClientRect();
        const linkRect = links[index].getBoundingClientRect();
        const x = (linkRect.x - pageRect.x + 175) / 6 - 1;
        const y = (linkRect.y - pageRect.y + 70) / 6 - 1;
        const width = linkRect.width / 6 + 2;
        const height = linkRect.height / 6 + 2;

        doc.link(x, y, width, height, { url: links[index].href });
      }

      if (i !== images.length - 1) {
        doc.addPage();
      }
    }

    return doc;
  }

  /**
   * html2canvas clones the whole document on every call, so capturing page by page costs the report
   * once per page. Pages are captured in as few passes as the canvas size limit allows instead, with
   * the pages outside the current pass hidden, and each pass is then sliced back into single pages.
   */
  private static async renderPages(elements: HTMLElement[]): Promise<PageImage[]> {
    const images: PageImage[] = [];
    const previousDisplay = elements.map((element) => element.style.display);

    try {
      for (const group of this.groupPages(elements)) {
        elements.forEach((element, index) => {
          element.style.display = group.pages.includes(element) ? previousDisplay[index] : 'none';
        });

        const canvas = await html2canvas(group.container, {
          scrollX: 0,
          scrollY: 0,
          windowWidth: 2000,
          windowHeight: 3000,
          removeContainer: true,
          scale: 1,
        });

        const containerTop = group.container.getBoundingClientRect().top;

        for (const page of group.pages) {
          const rect = page.getBoundingClientRect();
          images.push(this.cropPage(canvas, rect.top - containerTop, rect.width, rect.height));
        }

        this.release(canvas);
      }
    } finally {
      elements.forEach((element, index) => (element.style.display = previousDisplay[index]));
    }

    return images;
  }

  /** A pass covers consecutive pages of one container, up to what a single canvas can hold. */
  private static groupPages(elements: HTMLElement[]): { container: HTMLElement; pages: HTMLElement[] }[] {
    const groups: { container: HTMLElement; pages: HTMLElement[] }[] = [];
    let current: { container: HTMLElement; pages: HTMLElement[] } | null = null;
    let pixels = 0;

    for (const element of elements) {
      const container = element.parentElement ?? element;
      const rect = element.getBoundingClientRect();
      const elementPixels = rect.width * rect.height;

      if (!current || current.container !== container || pixels + elementPixels > this.MAX_CANVAS_PIXELS) {
        current = { container, pages: [] };
        groups.push(current);
        pixels = 0;
      }

      current.pages.push(element);
      pixels += elementPixels;
    }

    return groups;
  }

  /** Encoded straight away so a report never holds more than one page bitmap besides its pass. */
  private static cropPage(source: HTMLCanvasElement, top: number, width: number, height: number): PageImage {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));

    const context = canvas.getContext('2d');

    if (!context) {
      this.release(canvas);
      throw new Error('Failed to acquire a 2D context while cropping a report page');
    }

    context.drawImage(source, 0, Math.round(top), canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);

    const image = { data: canvas.toDataURL('image/jpeg', 0.75), width: canvas.width, height: canvas.height };
    this.release(canvas);

    return image;
  }

  private static release(canvas: HTMLCanvasElement): void {
    canvas.width = 0;
    canvas.height = 0;
  }

  private static extractElementOpeningTags(html: HTMLElement | Node, ancestors = false): string {
    const ancestorTagsString =
      ancestors &&
      (!(html instanceof HTMLElement) || html.id !== 'temporary_root') &&
      html.parentElement &&
      html.parentElement.id !== 'temporary_root'
        ? this.extractElementOpeningTags(html.parentElement, ancestors)
        : '';

    if (html instanceof HTMLElement) {
      const strings = html.outerHTML.split(/[<>]/gm);
      return ancestorTagsString + '<' + strings[1] + '>';
    } else {
      const span = document.createElement('span');
      span.innerText = html.textContent;
      return ancestorTagsString + span.innerHTML;
    }
  }

  private static extractElementClosingTags(html: HTMLElement | Node, ancestors = false): string {
    const ancestorTagsString =
      ancestors &&
      (!(html instanceof HTMLElement) || html.id !== 'temporary_root') &&
      html.parentElement &&
      html.parentElement.id !== 'temporary_root'
        ? this.extractElementClosingTags(html.parentElement, ancestors)
        : '';

    if (html instanceof HTMLElement) {
      if (html instanceof HTMLBRElement) {
        return ancestorTagsString;
      }

      const tag = html.tagName.toLocaleLowerCase();
      return '</' + tag + '>' + ancestorTagsString;
    } else {
      return ancestorTagsString;
    }
  }
}
